// services/apiV2.ts — client wrappers for redesign endpoints
import { apiFetchWithSession } from './api';
import { usoAcao } from '../v2/uso';
import {
  AuthSession, BookV2, ProfileV2, StreakData, HeatmapData,
  AnalyticsData, AchievementsResponse, InsightsResponse, DailyProgressV2, ShelfV2
} from '../types';

const API = '/api';

/** O acesso ao app (o modelo pago com 7 dias grátis, 01/11/2026 — server/acesso-app.js). */
export interface AcessoApp {
  estado: 'pro' | 'teste' | 'bloqueado' | 'isento';
  dias_restantes: number | null;
  fim_teste: string | null;
  motivo: string | null;
  /** motivo 'espera': os dias do teste só contam da 1ª página; até aqui a conta espera. */
  espera_ate?: string | null;
  teste_dias?: number;
}

/** Mentor do Livro: um item já liberado da trilha (server/mentor-livro). */
export type TrilhaAvaliacao = 'na_hora' | 'cansou' | 'faltou';
export type TrilhaItem = {
  id: string; tipo: 'nota' | 'analise'; pagina_gatilho: number; titulo: string | null;
  corpo: string; ordem: number; liberado_em: string;
  avaliacao: TrilhaAvaliacao | null; avaliado_em: string | null;
  /** null = liberado e ainda não aberto (o selo "nova"). */
  visto_em?: string | null;
  /** A Travessia: a forma do item, a região do mapa e o extra (vídeo, desafio diário). */
  forma?: TravessiaForma; etapa?: number | null;
  /** imagem (02/11/2026): a foto, o mapa ou o recorte de notícia da parada (comentário, insight ou desafio). */
  extra?: { diario?: boolean; duracao?: string; tipo?: string; url?: string; imagem?: { src: string; legenda?: string; credito?: string } } | null;
  desafio?: DesafioEstado;
};
export type TravessiaForma = 'comentario' | 'insight' | 'desafio' | 'video' | 'marco' | 'fechamento';
/** hoje: o que a pessoa marcou hoje (null = ainda nada); sequencia: dias seguidos feitos até hoje/ontem. */
export type DesafioEstado = { hoje: boolean | null; ultimo: boolean | null; ultimo_dia: string | null; feitos: number; sequencia: number };
export type TravessiaEtapa = {
  n: number; nome: string; terreno: string; de: number; ate: number;
  resumo: string;
  /** O fundo de imagens da etapa (1 a 4): sem ele, o terreno desenhado. */
  fundos?: TravessiaFundo[];
};
/** Uma imagem do fundo da etapa: o arquivo, o enquadramento (object-position)
 *  e a legenda curta ("Título: por que está aqui"). */
export type TravessiaFundo = { src: string; foco: string; legenda?: string };
export type TravessiaFicha = {
  gancho: string; resultados: string[]; para_quem: string[]; nao_para_quem: string[];
  na_trilha: string[]; desafio_exemplo: { titulo: string; corpo: string };
  tempo: string; esforco: string; amazon: string;
  depois: { titulo: string; autor: string; porque: string }[];
  creditos_imagens?: string;
};
export type TravessiaMapa = {
  book_id: string; pagina: number; em_paginas: boolean;
  livro: { titulo: string; autor: string; nome?: string; total_paginas: number; ficha: TravessiaFicha; etapas: TravessiaEtapa[] };
  itens: TrilhaItem[];
  /** Sob a névoa: só a forma, a página e a etapa. */
  adiante: { forma: TravessiaForma; pagina: number; etapa: number | null }[];
  /** 'etapa1' = a 1ª etapa grátis (sem o Pro); 'total' = o caminho inteiro. */
  acesso?: 'total' | 'etapa1';
  /** Sem o Pro: as etapas seguintes, só o nome e quantas paradas têm. */
  trancadas?: { n: number; nome: string; paradas: number }[];
  etapa1_concluida?: boolean;
};

/** A Home nova: a próxima parada e o último comentário aberto de cada livro com Travessia. */
export type TravessiaResumo = {
  nao_vistos: number; pagina?: number; total?: number; etapas?: number; concluida?: boolean;
  etapa?: { n: number; nome: string } | null;
  proxima?: { pagina: number; forma: TravessiaForma; trancada?: boolean } | null;
  ultimo?: { forma: TravessiaForma; pagina: number; texto: string } | null;
  desafios_feitos?: number; desafios_total?: number;
  acesso?: 'total' | 'etapa1'; etapa1_concluida?: boolean;
  /** O insight/desafio mais recente liberado num dia anterior a hoje (a Home lembra dele). */
  levou?: { id: string; forma: 'insight' | 'desafio'; titulo: string; pagina: number; ontem: boolean } | null;
};
/** A curadoria (server/curadoria): o cartão de um livro na vitrine. */
export type CuradoriaLivro = {
  slug: string; titulo: string; autor: string; capa: string | null; paginas: number | null;
  compra: string | null; temporada: boolean; travessia: 'disponivel' | 'em_breve' | null;
  etapas: number | null; nota: string | null; habilidade?: string | null;
};
export type CuradoriaNaEstante = { id: string; status: string; current_page: number; total_pages: number; no_mapa: boolean };
export type Curadoria = {
  temporada: { n: number; nome: string; titulo: string; frase: string; renovacao: string; livros: CuradoriaLivro[] };
  biografias?: { nome: string; titulo: string; frase: string; livros: CuradoriaLivro[] } | null;
  objetivos: { id: string; nome: string; frase: string; livros: CuradoriaLivro[] }[];
  votos: string[]; acesso_travessia: boolean; etapa1_gratis?: boolean; na_estante: Record<string, CuradoriaNaEstante>;
};
export type CuradoriaFicha = {
  livro: CuradoriaLivro; votei: boolean; acesso_travessia: boolean; etapa1_gratis?: boolean; meu_livro: CuradoriaNaEstante | null;
  ficha: null | (Omit<TravessiaFicha, 'amazon'> & { nome: string; total_paginas: number; etapas: Omit<TravessiaEtapa, 'terreno'>[] });
};

async function ok<T = any>(r: Response): Promise<T> {
  const text = await r.text();
  const data = text ? (() => { try { return JSON.parse(text); } catch { return {}; } })() : {};
  if (!r.ok) {
    const msg = (data && (data as any).error) || `HTTP ${r.status}`;
    const err = new Error(msg);
    // O status vai junto: sem ele quem chama só tem a frase do servidor, e
    // "não existe" (404) e "é fechado" (403) pedem telas diferentes.
    (err as any).status = r.status;
    // E o corpo: no 409 do caderno ele traz a versão que está no servidor, e
    // é com ela que o cliente resolve o conflito sem perder nada.
    (err as any).dados = data;
    throw err;
  }
  return data as T;
}

// SSE autenticado: EventSource não manda header de auth, então o stream é lido
// à mão pelo fetch. Chama onEvent(evento, dados) a cada bloco e devolve a função
// de cancelamento. Se streaming não rolar (proxy, rede), sai calado — quem
// chamou cai pro polling.
function sseStream(session: AuthSession | null, url: string, onEvent: (event: string, data: any) => void): () => void {
  const ctrl = new AbortController();
  (async () => {
    try {
      const r = await apiFetchWithSession(session, url, {
        signal: ctrl.signal, headers: { Accept: 'text/event-stream' },
      });
      if (!r.body || !r.body.getReader) return;
      const reader = r.body.getReader();
      const dec = new TextDecoder();
      let buf = '';
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        let idx;
        while ((idx = buf.indexOf('\n\n')) >= 0) {
          const chunk = buf.slice(0, idx); buf = buf.slice(idx + 2);
          let ev = 'message'; let data = '';
          chunk.split('\n').forEach(line => {
            if (line.startsWith('event:')) ev = line.slice(6).trim();
            else if (line.startsWith('data:')) data += line.slice(5).trim();
          });
          if (data) { try { onEvent(ev, JSON.parse(data)); } catch {} }
        }
      }
    } catch { /* abort / rede — o caller cai pra polling se precisar */ }
  })();
  return () => { try { ctrl.abort(); } catch {} };
}

/** Uma trilha na oferta pública, como habilidade (server/vitrine, 03/11/2026). */
export type TrilhaDaOferta = {
  slug: string; titulo: string; autor: string; capa: string | null;
  habilidade?: string; area?: string | null; palavra?: string; promessa?: string | null;
  desafios?: number | null; etapas?: number;
};

export const api = {
  // Profile
  async getProfile(session: AuthSession | null): Promise<ProfileV2> {
    const r = await apiFetchWithSession(session, `${API}/profile`);
    return ok<ProfileV2>(r);
  },
  async patchProfile(session: AuthSession | null, update: Partial<Pick<ProfileV2, 'phone' | 'palette' | 'display_name'>> & { bio?: string | null; is_private?: boolean; username?: string }): Promise<ProfileV2> {
    const r = await apiFetchWithSession(session, `${API}/profile`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(update),
    });
    return ok<ProfileV2>(r);
  },

  // RevenueCat — AUTO-CURA do plano: o backend consulta o RevenueCat (chave secreta)
  // e sincroniza profiles.plan pelos direitos ATIVOS. Chamado em background (boot,
  // volta de foco, pos-compra). NUNCA lanca — falha = silencio, tenta de novo depois.
  // ── INDICAÇÃO ────────────────────────────────────────────────────────
  // Amarra quem acabou de entrar a quem indicou. Idempotente no backend.
  async attachReferral(session: AuthSession | null, code: string): Promise<any> {
    const r = await apiFetchWithSession(session, `${API}/referrals/attach`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code }),
    });
    return ok<any>(r);
  },

  // Progresso do card indique-e-ganhe: link, bolinhas, preços por tier.
  async getReferrals(session: AuthSession | null): Promise<any> {
    const r = await apiFetchWithSession(session, `${API}/referrals/me`);
    return ok<any>(r);
  },

  // Estado de acesso (fundador/promo/trial/assinante) + preço final.
  async getAccess(session: AuthSession | null): Promise<any> {
    const r = await apiFetchWithSession(session, `${API}/me/access`);
    return ok<any>(r);
  },

  async syncRcPlan(session: AuthSession | null): Promise<{ ok: boolean; plan?: string | null; changed?: boolean; mentor?: any }> {
    try {
      const r = await apiFetchWithSession(session, `${API}/rc/sync-plan`, { method: 'POST' });
      if (!r.ok) return { ok: false };
      return await r.json();
    } catch { return { ok: false }; }
  },

  // ── Social (perfil publico, follows, blocks, report) ──
  async getUserProfile(session: AuthSession | null, idOrUsername: string): Promise<any> {
    const r = await apiFetchWithSession(session, `${API}/users/${encodeURIComponent(idOrUsername)}`);
    return ok(r);
  },
  // Leve: só contadores + flags de relação (poll de tempo real do perfil).
  async userSocialStatus(session: AuthSession | null, idOrUsername: string): Promise<any> {
    const r = await apiFetchWithSession(session, `${API}/users/${encodeURIComponent(idOrUsername)}/social-status`);
    return ok(r);
  },
  // Busca de pessoas por nome/@username (só aparece ao pesquisar na Explorar).
  async searchUsers(session: AuthSession | null, q: string): Promise<{ users: any[] }> {
    const r = await apiFetchWithSession(session, `${API}/users/search?q=${encodeURIComponent(q)}`);
    return ok(r);
  },
  async followUser(session: AuthSession | null, id: string): Promise<any> {
    const r = await apiFetchWithSession(session, `${API}/users/${encodeURIComponent(id)}/follow`, { method: 'POST' });
    return ok(r);
  },
  async unfollowUser(session: AuthSession | null, id: string): Promise<any> {
    const r = await apiFetchWithSession(session, `${API}/users/${encodeURIComponent(id)}/follow`, { method: 'DELETE' });
    return ok(r);
  },
  async listFollowers(session: AuthSession | null, id: string): Promise<any> {
    const r = await apiFetchWithSession(session, `${API}/users/${encodeURIComponent(id)}/followers`);
    return ok(r);
  },
  async listFollowing(session: AuthSession | null, id: string): Promise<any> {
    const r = await apiFetchWithSession(session, `${API}/users/${encodeURIComponent(id)}/following`);
    return ok(r);
  },
  async blockUser(session: AuthSession | null, id: string): Promise<any> {
    const r = await apiFetchWithSession(session, `${API}/users/${encodeURIComponent(id)}/block`, { method: 'POST' });
    return ok(r);
  },
  async unblockUser(session: AuthSession | null, id: string): Promise<any> {
    const r = await apiFetchWithSession(session, `${API}/users/${encodeURIComponent(id)}/block`, { method: 'DELETE' });
    return ok(r);
  },
  async reportUser(session: AuthSession | null, id: string, reason: string): Promise<any> {
    const r = await apiFetchWithSession(session, `${API}/users/${encodeURIComponent(id)}/report`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ reason }),
    });
    return ok(r);
  },
  async listFollowRequests(session: AuthSession | null): Promise<any> {
    const r = await apiFetchWithSession(session, `${API}/follow-requests`);
    return ok(r);
  },
  async approveFollowRequest(session: AuthSession | null, id: string): Promise<any> {
    const r = await apiFetchWithSession(session, `${API}/follow-requests/${encodeURIComponent(id)}/approve`, { method: 'POST' });
    return ok(r);
  },
  async rejectFollowRequest(session: AuthSession | null, id: string): Promise<any> {
    const r = await apiFetchWithSession(session, `${API}/follow-requests/${encodeURIComponent(id)}`, { method: 'DELETE' });
    return ok(r);
  },
  async followingActivity(session: AuthSession | null): Promise<any> {
    const r = await apiFetchWithSession(session, `${API}/following/activity`);
    return ok(r);
  },
  async uploadAvatar(session: AuthSession | null, file: File): Promise<{ url: string }> {
    const fd = new FormData();
    fd.append('avatar', file);
    const r = await apiFetchWithSession(session, `${API}/profile/avatar`, { method: 'POST', body: fd });
    return ok(r);
  },
  // ── Stories ──
  async clubStories(session: AuthSession | null, clubId: string): Promise<any> {
    const r = await apiFetchWithSession(session, `${API}/clubs/${clubId}/stories`);
    return ok(r);
  },
  async postStories(session: AuthSession | null, clubId: string, stories: any[], visibility?: string): Promise<any> {
    const r = await apiFetchWithSession(session, `${API}/clubs/${clubId}/stories`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ stories, visibility }),
    });
    return ok(r);
  },
  async uploadStoryPhoto(session: AuthSession | null, file: File): Promise<{ url: string }> {
    const fd = new FormData();
    fd.append('photo', file);
    const r = await apiFetchWithSession(session, `${API}/stories/photo`, { method: 'POST', body: fd });
    return ok(r);
  },
  /**
   * Manda um vídeo (gravado na hora ou vindo da galeria) e recebe de volta a fila
   * já cortada em pedaços de 60s — no máximo 5. Quem corta é o servidor: um vídeo
   * de 4 minutos volta como 4 stories, prontos pra reordenar antes de publicar.
   * Demora (transcode), então quem chama precisa mostrar progresso.
   */
  async uploadStoryVideo(session: AuthSession | null, file: File): Promise<{ stories: Array<{ videoUrl: string; posterUrl: string | null; durationMs: number }> }> {
    const fd = new FormData();
    fd.append('video', file);
    const r = await apiFetchWithSession(session, `${API}/stories/video`, { method: 'POST', body: fd });
    return ok(r);
  },

  // ── CADERNOS (reforma do caderno de estudos) ──────────────────────
  // Todas degradam sozinhas: sem a migration o servidor responde
  // `disponivel: false` com listas vazias, e a tela mostra estado vazio.
  async listCadernos(session: AuthSession | null): Promise<any> {
    return ok<any>(await apiFetchWithSession(session, `${API}/cadernos`));
  },
  async criarCaderno(session: AuthSession | null, dados: { name: string; description?: string; icon?: string }): Promise<any> {
    return ok<any>(await apiFetchWithSession(session, `${API}/cadernos`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(dados),
    }));
  },
  async editarCaderno(session: AuthSession | null, id: string, dados: any): Promise<any> {
    return ok<any>(await apiFetchWithSession(session, `${API}/cadernos/${id}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(dados),
    }));
  },
  /** destino: 'entrada' (move as notas) ou 'lixeira' (manda junto). */
  async apagarCaderno(session: AuthSession | null, id: string, destino: 'entrada' | 'lixeira' = 'entrada'): Promise<any> {
    return ok<any>(await apiFetchWithSession(session, `${API}/cadernos/${id}?destino=${destino}`, { method: 'DELETE' }));
  },
  /** Junta num documento só as folhas que a paginação antiga tinha partido.
   *  O texto vai juntado DAQUI porque só o cliente sabe converter folha em
   *  markdown, em html e em documento — ver `v2/doc.ts`. */
  async unificarCaderno(session: AuthSession | null, id: string, dados: {
    text: any; manter: string; remover: string[];
  }): Promise<any> {
    return ok<any>(await apiFetchWithSession(session, `${API}/cadernos/${id}/unificar`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(dados),
    }));
  },
  async listarNotas(session: AuthSession | null, filtros: Record<string, string> = {}): Promise<any> {
    const qs = new URLSearchParams(filtros).toString();
    return ok<any>(await apiFetchWithSession(session, `${API}/notas${qs ? '?' + qs : ''}`));
  },
  async moverNota(session: AuthSession | null, id: string, notebookId: string): Promise<any> {
    return ok<any>(await apiFetchWithSession(session, `${API}/notas/${id}/mover`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ notebookId }),
    }));
  },
  async restaurarNota(session: AuthSession | null, id: string): Promise<any> {
    return ok<any>(await apiFetchWithSession(session, `${API}/notas/${id}/restaurar`, { method: 'POST' }));
  },
  async esvaziarLixeira(session: AuthSession | null): Promise<any> {
    return ok<any>(await apiFetchWithSession(session, `${API}/lixeira`, { method: 'DELETE' }));
  },
  async criarNotaLivre(session: AuthSession | null, dados: {
    notebookId?: string; title?: string; text: string;
    /** Tipografia e posição da FOLHA — cada nota do caderno pessoal é uma. */
    font?: string; fontSize?: string; ordem?: number;
    type?: string; color?: string; bookId?: string; page?: number | null;
    audioUrl?: string; audioDur?: number; wave?: number[]; photoUrl?: string; photoKind?: string;
  }): Promise<any> {
    return ok<any>(await apiFetchWithSession(session, `${API}/notas`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(dados),
    }));
  },

  // ── FASE 2 · editor, tags e links ─────────────────────────────────
  /** O editor completo. Separado de updateBookNote (4 campos) porque aquele
   *  é o contrato do app das lojas e não muda. */
  async editarNota(session: AuthSession | null, id: string, dados: any, extra: RequestInit = {}): Promise<any> {
    return ok<any>(await apiFetchWithSession(session, `${API}/notas/${id}`, {
      ...extra,
      method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(dados),
    }));
  },
  async listarTags(session: AuthSession | null): Promise<any> {
    return ok<any>(await apiFetchWithSession(session, `${API}/tags`));
  },
  async renomearTag(session: AuthSession | null, id: string, name: string): Promise<any> {
    return ok<any>(await apiFetchWithSession(session, `${API}/tags/${id}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name }),
    }));
  },
  async apagarTag(session: AuthSession | null, id: string): Promise<any> {
    return ok<any>(await apiFetchWithSession(session, `${API}/tags/${id}`, { method: 'DELETE' }));
  },
  /** "Mencionada em" — quem cita esta nota. */
  async mencoesDaNota(session: AuthSession | null, id: string): Promise<any> {
    return ok<any>(await apiFetchWithSession(session, `${API}/notas/${id}/mencoes`));
  },
  /** Autocompletar do [[ ]]. */
  async buscarNotas(session: AuthSession | null, q: string): Promise<any> {
    return ok<any>(await apiFetchWithSession(session, `${API}/notas/buscar?q=${encodeURIComponent(q)}`));
  },

  // FP4 · Caderno de estudos pessoal (book_notes)
  /** O acervo inteiro: todas as anotacoes de todos os livros pessoais.
   *  Mesma base da tela por livro — clubes ficam de fora (o backend filtra). */
  async listAllNotes(session: AuthSession | null): Promise<{ notes: any[]; books: any[] }> {
    const r = await apiFetchWithSession(session, `${API}/v2/notes`);
    return ok<{ notes: any[]; books: any[] }>(r);
  },
  async listBookNotes(session: AuthSession | null, bookId: string): Promise<any[]> {
    const r = await apiFetchWithSession(session, `${API}/v2/books/${bookId}/notes`);
    return ok<any[]>(r);
  },
  async createBookNote(session: AuthSession | null, bookId: string, data: any): Promise<any> {
    const r = await apiFetchWithSession(session, `${API}/v2/books/${bookId}/notes`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data),
    });
    return ok(r);
  },
  async updateBookNote(session: AuthSession | null, id: string, data: any): Promise<any> {
    const r = await apiFetchWithSession(session, `${API}/v2/notes/${id}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data),
    });
    return ok(r);
  },
  async deleteBookNote(session: AuthSession | null, id: string): Promise<{ ok: boolean }> {
    const r = await apiFetchWithSession(session, `${API}/v2/notes/${id}`, { method: 'DELETE' });
    return ok(r);
  },
  async uploadBookNoteAudio(session: AuthSession | null, blob: Blob, filename = 'nota.webm'): Promise<{ url: string }> {
    const fd = new FormData();
    fd.append('audio', blob, filename);
    const r = await apiFetchWithSession(session, `${API}/book-notes/audio`, { method: 'POST', body: fd });
    return ok(r);
  },
  async uploadBookNotePhoto(session: AuthSession | null, file: File): Promise<{ url: string }> {
    const fd = new FormData();
    fd.append('photo', file);
    const r = await apiFetchWithSession(session, `${API}/book-notes/photo`, { method: 'POST', body: fd });
    return ok(r);
  },
  async viewStory(session: AuthSession | null, id: string): Promise<any> {
    const r = await apiFetchWithSession(session, `${API}/stories/${id}/view`, { method: 'POST' });
    return ok(r);
  },
  async likeStory(session: AuthSession | null, id: string): Promise<any> {
    const r = await apiFetchWithSession(session, `${API}/stories/${id}/like`, { method: 'POST' });
    return ok(r);
  },
  async unlikeStory(session: AuthSession | null, id: string): Promise<any> {
    const r = await apiFetchWithSession(session, `${API}/stories/${id}/like`, { method: 'DELETE' });
    return ok(r);
  },
  async deleteStory(session: AuthSession | null, id: string): Promise<any> {
    const r = await apiFetchWithSession(session, `${API}/stories/${id}`, { method: 'DELETE' });
    return ok(r);
  },
  async reportStory(session: AuthSession | null, id: string, reason: string): Promise<any> {
    const r = await apiFetchWithSession(session, `${API}/stories/${id}/report`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ reason }),
    });
    return ok(r);
  },
  async storyViewers(session: AuthSession | null, id: string): Promise<any> {
    const r = await apiFetchWithSession(session, `${API}/stories/${id}/viewers`);
    return ok(r);
  },
  // stories ativos de um usuário cruzando todos os clubes visíveis ao viewer (perfil)
  async userStories(session: AuthSession | null, userId: string): Promise<any> {
    const r = await apiFetchWithSession(session, `${API}/users/${encodeURIComponent(userId)}/stories`);
    return ok(r);
  },
  // bandeja da HOME: stories dos meus clubes + públicos de quem sigo + os meus (HM5)
  async homeStories(session: AuthSession | null): Promise<any> {
    const r = await apiFetchWithSession(session, `${API}/stories/home-feed`);
    return ok(r);
  },
  // publica story(s) PÚBLICO(s) sem clube, direto da home (HM5/HM7)
  async postHomeStory(session: AuthSession | null, stories: any[]): Promise<any> {
    const r = await apiFetchWithSession(session, `${API}/stories/home`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ stories }),
    });
    return ok(r);
  },
  async markTutorialSeen(session: AuthSession | null, seen = true) {
    const r = await apiFetchWithSession(session, `${API}/profile/tutorial-seen`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ seen }),
    });
    return ok(r);
  },
  // Estado por-tutorial (visto/habilitado) — onboarding em etapas (O1)
  async setTutorialState(session: AuthSession | null, key: string, patch: { seen?: boolean; enabled?: boolean }) {
    const r = await apiFetchWithSession(session, `${API}/profile/tutorial-state`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ key, ...patch }),
    });
    return ok(r);
  },

  // Círculo do Mentor (server/circulo/routes.js)
  // A vitrine: a página para quem não tem conta, sem login (server/circulo/vitrine.js).
  async getMentorVitrine(slug: string): Promise<any> {
    const r = await fetch(`${API}/mentores/${encodeURIComponent(slug)}/vitrine`);
    return ok(r);
  },
  // Mentores com série publicada (o cartão do Círculo na Home).
  async getMentores(session: AuthSession | null): Promise<{ mentores: any[] }> {
    const r = await apiFetchWithSession(session, `${API}/mentores`);
    return ok(r);
  },
  // O Círculo de quem pede, se for Mentor (a entrada na aba Eu e na Home).
  async getMeuCirculo(session: AuthSession | null): Promise<{ mentor: { slug: string; nome: string; serie: { titulo: string } | null } | null }> {
    const r = await apiFetchWithSession(session, `${API}/circulo/meu`);
    return ok(r);
  },
  async getMentor(session: AuthSession | null, slug: string): Promise<any> {
    const r = await apiFetchWithSession(session, `${API}/mentores/${encodeURIComponent(slug)}`);
    return ok(r);
  },
  // Antes de abrir a loja: 409 (motivo sem_serie_publicada) se o Mentor ainda não tem o que vender.
  async podeAssinarMentor(session: AuthSession | null, slug: string): Promise<{ ok: boolean }> {
    const r = await apiFetchWithSession(session, `${API}/mentores/${encodeURIComponent(slug)}/assinar`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}',
    });
    return ok(r);
  },
  async getMentorSerie(session: AuthSession | null, slug: string, serieId: string): Promise<any> {
    const r = await apiFetchWithSession(session, `${API}/mentores/${encodeURIComponent(slug)}/series/${serieId}`);
    return ok(r);
  },
  async getEpisodioVideo(session: AuthSession | null, slug: string, epId: string): Promise<{ url: string; caminho: string; expira_em: string; duracao_s: number | null; largura: number | null; altura: number | null }> {
    const r = await apiFetchWithSession(session, `${API}/mentores/${encodeURIComponent(slug)}/episodios/${epId}/video`);
    return ok(r);
  },
  async salvarProgressoEpisodio(session: AuthSession | null, slug: string, epId: string, segundos: number, concluido = false) {
    const r = await apiFetchWithSession(session, `${API}/mentores/${encodeURIComponent(slug)}/episodios/${epId}/progresso`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ segundos, concluido }),
    });
    return ok(r);
  },
  // As abas Clube/Estudo e Cadernos da página do Mentor (server/circulo/abas.js).
  async getMentorClube(session: AuthSession | null, slug: string): Promise<any> {
    const r = await apiFetchWithSession(session, `${API}/mentores/${encodeURIComponent(slug)}/clube`);
    return ok(r);
  },
  async getMentorCaderno(session: AuthSession | null, slug: string): Promise<any> {
    const r = await apiFetchWithSession(session, `${API}/mentores/${encodeURIComponent(slug)}/caderno`);
    return ok(r);
  },
  async criarMentorCaderno(session: AuthSession | null, slug: string): Promise<any> {
    const r = await apiFetchWithSession(session, `${API}/mentores/${encodeURIComponent(slug)}/caderno`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}',
    });
    return ok(r);
  },

  // O Editor do Mentor (server/circulo/editor.js). Só o próprio Mentor: os
  // outros recebem 403 so_o_mentor. O 400 traz { error, campo }.
  async getEditor(session: AuthSession | null): Promise<any> {
    const r = await apiFetchWithSession(session, `${API}/circulo/editor/eu`);
    return ok(r);
  },
  async salvarPerfilEditor(session: AuthSession | null, campos: Record<string, string | null>): Promise<any> {
    const r = await apiFetchWithSession(session, `${API}/circulo/editor/perfil`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(campos),
    });
    const d = await ok(r);
    usoAcao('editor_salvar_perfil', { campos: Object.keys(campos) });
    return d;
  },
  // Modelo, paleta e seções da página (page_layout, page_palette, page_sections; só o que mudou).
  async salvarPaginaEditor(session: AuthSession | null, campos: Record<string, any>): Promise<any> {
    const r = await apiFetchWithSession(session, `${API}/circulo/editor/pagina`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(campos),
    });
    const d = await ok(r);
    usoAcao('editor_salvar_pagina', { campos: Object.keys(campos) });
    return d;
  },
  // A foto já recortada; só vale depois do PATCH do perfil com o photo_url.
  async enviarFotoEditor(session: AuthSession | null, foto: Blob): Promise<{ photo_url: string }> {
    const r = await apiFetchWithSession(session, `${API}/circulo/editor/foto`, {
      method: 'POST', headers: { 'Content-Type': foto.type || 'image/jpeg' }, body: foto,
    });
    return ok(r);
  },
  // A aba Série do editor (server/circulo/editor-serie.js).
  async getSeriesEditor(session: AuthSession | null): Promise<any> {
    const r = await apiFetchWithSession(session, `${API}/circulo/editor/series`);
    return ok(r);
  },
  async getSerieEditor(session: AuthSession | null, id: string): Promise<any> {
    const r = await apiFetchWithSession(session, `${API}/circulo/editor/series/${encodeURIComponent(id)}`);
    return ok(r);
  },
  // Sem id cria; com id grava só os campos que vierem.
  async salvarSerieEditor(session: AuthSession | null, id: string | null, campos: Record<string, any>): Promise<any> {
    const r = await apiFetchWithSession(session, `${API}/circulo/editor/series${id ? `/${encodeURIComponent(id)}` : ''}`, {
      method: id ? 'PATCH' : 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(campos),
    });
    const d = await ok(r);
    usoAcao('editor_salvar_serie', { nova: !id, campos: Object.keys(campos) });
    return d;
  },
  // A foto do exemplar já recortada; só vale depois de gravar a série com o cover_url.
  async enviarCapaEditor(session: AuthSession | null, foto: Blob): Promise<{ cover_url: string }> {
    const r = await apiFetchWithSession(session, `${API}/circulo/editor/capa`, {
      method: 'POST', headers: { 'Content-Type': foto.type || 'image/jpeg' }, body: foto,
    });
    return ok(r);
  },
  async buscarEstanteEditor(session: AuthSession | null, q: string): Promise<any> {
    const r = await apiFetchWithSession(session, `${API}/circulo/editor/estante?q=${encodeURIComponent(q)}`);
    return ok(r);
  },
  async criarEpisodioEditor(session: AuthSession | null, serieId: string, dados: { titulo: string; sinopse?: string | null }): Promise<any> {
    const r = await apiFetchWithSession(session, `${API}/circulo/editor/series/${encodeURIComponent(serieId)}/episodios`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(dados),
    });
    return ok(r);
  },
  async editarEpisodioEditor(session: AuthSession | null, epId: string, dados: { titulo?: string; sinopse?: string | null }): Promise<any> {
    const r = await apiFetchWithSession(session, `${API}/circulo/editor/episodios/${encodeURIComponent(epId)}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(dados),
    });
    return ok(r);
  },
  async ordenarEpisodiosEditor(session: AuthSession | null, serieId: string, ids: string[]): Promise<any> {
    const r = await apiFetchWithSession(session, `${API}/circulo/editor/series/${encodeURIComponent(serieId)}/ordem`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ids }),
    });
    return ok(r);
  },
  // Com vídeo, assistido ou comentado a API pede confirmado (409 precisa_confirmar).
  async apagarEpisodioEditor(session: AuthSession | null, epId: string, confirmado = false): Promise<any> {
    const r = await apiFetchWithSession(session, `${API}/circulo/editor/episodios/${encodeURIComponent(epId)}${confirmado ? '?confirmado=1' : ''}`, {
      method: 'DELETE',
    });
    return ok(r);
  },

  // A aba Clube do editor (server/circulo/editor-clube.js). Criar o clube é
  // createClub (POST /api/clubs); aqui só se liga ou desliga (club_id null).
  async getClubesEditor(session: AuthSession | null): Promise<any> {
    const r = await apiFetchWithSession(session, `${API}/circulo/editor/clubes`);
    return ok(r);
  },
  async ligarClubeEditor(session: AuthSession | null, serieId: string, clubId: string | null): Promise<any> {
    const r = await apiFetchWithSession(session, `${API}/circulo/editor/series/${encodeURIComponent(serieId)}/clube`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ club_id: clubId }),
    });
    const d = await ok(r);
    usoAcao('editor_salvar_clube', { ligou: !!clubId });
    return d;
  },

  // "Meus episódios" do Mentor (server/circulo/estudio.js). O envio do vídeo
  // em pedaços fica em v2/circulo/envio-video.ts.
  async getEstudio(session: AuthSession | null, slug: string, serieId?: string): Promise<any> {
    const q = serieId ? `?serie=${encodeURIComponent(serieId)}` : '';
    const r = await apiFetchWithSession(session, `${API}/mentores/${encodeURIComponent(slug)}/estudio${q}`);
    return ok(r);
  },
  async criarEpisodio(session: AuthSession | null, slug: string, serieId: string, dados: any): Promise<any> {
    const r = await apiFetchWithSession(session, `${API}/mentores/${encodeURIComponent(slug)}/series/${serieId}/episodios`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(dados),
    });
    return ok(r);
  },
  async editarEpisodio(session: AuthSession | null, slug: string, epId: string, dados: any): Promise<any> {
    const r = await apiFetchWithSession(session, `${API}/mentores/${encodeURIComponent(slug)}/episodios/${epId}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(dados),
    });
    return ok(r);
  },
  async publicarEpisodio(session: AuthSession | null, slug: string, epId: string, publicar: boolean): Promise<any> {
    const r = await apiFetchWithSession(session, `${API}/mentores/${encodeURIComponent(slug)}/episodios/${epId}/${publicar ? 'publicar' : 'despublicar'}`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}',
    });
    return ok(r);
  },

  // Books (v2 format — sessions embedded)
  async getLibrary(session: AuthSession | null): Promise<BookV2[]> {
    const r = await apiFetchWithSession(session, `${API}/v2/library`);
    return ok<BookV2[]>(r);
  },
  async lookupISBN(session: AuthSession | null, isbn: string) {
    const clean = isbn.replace(/[^0-9Xx]/g, '');
    const r = await apiFetchWithSession(session, `${API}/books/isbn/${clean}`);
    return ok<{
      isbn: string; title: string; author: string; pages: number | null;
      coverSourceUrl: string | null; publisher: string | null;
      publishedDate: string | null; description: string | null; source: string;
    }>(r);
  },
  // Busca de livro por NOME (Google Books) — lista pra escolher e pré-preencher.
  async searchBooks(session: AuthSession | null, q: string) {
    const r = await apiFetchWithSession(session, `${API}/books/search?q=${encodeURIComponent(q)}`);
    return ok<{ results: Array<{ title: string; author: string; pages: number | null; coverSourceUrl: string | null; isbn: string | null; publishedDate: string | null; publisher?: string | null }> }>(r);
  },
  async createBook(session: AuthSession | null, book: {
    title: string; author: string; totalPages: number; dailyGoal?: number;
    coverHue?: number; coverStyle?: string; targetDate?: string | null;
    startedAt?: string | null; note?: string | null; status?: 'reading' | 'finished' | 'wishlist';
    coverSourceUrl?: string | null; isbn?: string | null;
    fromClubId?: string | null;
    progressUnit?: 'pages' | 'chapters' | 'positions';
    genre?: string | null; tags?: string[];
  }): Promise<BookV2> {
    const r = await apiFetchWithSession(session, `${API}/v2/books`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(book),
    });
    return ok<BookV2>(r);
  },
  async updateBook(session: AuthSession | null, id: string, update: any): Promise<BookV2> {
    const r = await apiFetchWithSession(session, `${API}/books/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(update),
    });
    return ok<BookV2>(r);
  },
  async deleteBook(session: AuthSession | null, id: string) {
    const r = await apiFetchWithSession(session, `${API}/books/${id}`, { method: 'DELETE' });
    return ok(r);
  },
  // ── FP3 — Estantes personalizadas ──
  async listShelves(session: AuthSession | null): Promise<ShelfV2[]> {
    const r = await apiFetchWithSession(session, `${API}/shelves`);
    return ok<ShelfV2[]>(r);
  },
  async createShelf(session: AuthSession | null, name: string, emoji?: string | null): Promise<ShelfV2> {
    const r = await apiFetchWithSession(session, `${API}/shelves`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name, emoji: emoji || null }),
    });
    return ok<ShelfV2>(r);
  },
  async updateShelf(session: AuthSession | null, id: string, patch: { name?: string; emoji?: string | null; sort?: number }): Promise<ShelfV2> {
    const r = await apiFetchWithSession(session, `${API}/shelves/${id}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(patch),
    });
    return ok<ShelfV2>(r);
  },
  async deleteShelf(session: AuthSession | null, id: string) {
    const r = await apiFetchWithSession(session, `${API}/shelves/${id}`, { method: 'DELETE' });
    return ok(r);
  },
  // A ordem das estantes vai inteira: arrastar uma move todas as outras, e
  // trinta requisições por gesto seria absurdo.
  async reorderShelves(session: AuthSession | null, ids: string[]): Promise<{ shelves: ShelfV2[] }> {
    const r = await apiFetchWithSession(session, `${API}/shelves/reorder`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ids }),
    });
    return ok(r);
  },
  async addBookToShelf(session: AuthSession | null, shelfId: string, bookId: string) {
    const r = await apiFetchWithSession(session, `${API}/shelves/${shelfId}/books`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ bookId }),
    });
    return ok(r);
  },
  async removeBookFromShelf(session: AuthSession | null, shelfId: string, bookId: string) {
    const r = await apiFetchWithSession(session, `${API}/shelves/${shelfId}/books/${bookId}`, { method: 'DELETE' });
    return ok(r);
  },
  // ── Página do Livro (objeto social): dados + review + comentários ──
  async bookPage(session: AuthSession | null, id: string): Promise<any> {
    const r = await apiFetchWithSession(session, `${API}/books/${encodeURIComponent(id)}/page`);
    return ok(r);
  },
  async listBookComments(session: AuthSession | null, id: string): Promise<{ comments: any[]; isBookOwner: boolean }> {
    const r = await apiFetchWithSession(session, `${API}/books/${encodeURIComponent(id)}/comments`);
    return ok(r);
  },
  async addBookComment(session: AuthSession | null, id: string, text: string, parentId?: string | null): Promise<any> {
    const r = await apiFetchWithSession(session, `${API}/books/${encodeURIComponent(id)}/comments`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text, parentId: parentId || null }),
    });
    return ok(r);
  },
  async editBookComment(session: AuthSession | null, id: string, cid: string, text: string): Promise<any> {
    const r = await apiFetchWithSession(session, `${API}/books/${encodeURIComponent(id)}/comments/${encodeURIComponent(cid)}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ text }),
    });
    return ok(r);
  },
  async deleteBookComment(session: AuthSession | null, id: string, cid: string): Promise<any> {
    const r = await apiFetchWithSession(session, `${API}/books/${encodeURIComponent(id)}/comments/${encodeURIComponent(cid)}`, { method: 'DELETE' });
    return ok(r);
  },
  async likeBookComment(session: AuthSession | null, id: string, cid: string): Promise<{ liked: boolean; count: number }> {
    const r = await apiFetchWithSession(session, `${API}/books/${encodeURIComponent(id)}/comments/${encodeURIComponent(cid)}/like`, { method: 'POST' });
    return ok(r);
  },
  async reportBookComment(session: AuthSession | null, id: string, cid: string, reason?: string): Promise<any> {
    const r = await apiFetchWithSession(session, `${API}/books/${encodeURIComponent(id)}/comments/${encodeURIComponent(cid)}/report`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ reason: reason || '' }),
    });
    return ok(r);
  },
  async setBookStatus(session: AuthSession | null, id: string, status: 'lendo' | 'lido' | 'proximo') {
    const r = await apiFetchWithSession(session, `${API}/books/${id}/status`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status }),
    });
    return ok(r);
  },

  async syncBookPage(session: AuthSession | null, bookId: string, page: number) {
    const r = await apiFetchWithSession(session, `${API}/books/${bookId}/sync-page`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ page }),
    });
    return ok<{ book: BookV2; syncedTo: number; trilhaNovas?: number }>(r);
  },

  // Reading logging — preserve "até qual página" workflow
  async logReadingToPage(session: AuthSession | null, bookId: string, page: number, minutes?: number | null) {
    const r = await apiFetchWithSession(session, `${API}/books/${bookId}/log-to-page`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(minutes != null ? { page, minutes } : { page }),
    });
    return ok<{ book: BookV2; entry: any; finished: boolean; trilhaNovas?: number }>(r);
  },

  // Mentor do Livro: a trilha liberada deste livro (a cópia do leitor) e a
  // avaliação de 1 toque (null desfaz). Ver server/mentor-livro.
  async trilhaDoLivro(session: AuthSession | null, bookId: string) {
    const r = await apiFetchWithSession(session, `${API}/livros/${bookId}/trilha`, { cache: 'no-store' });
    return ok<{ book_id: string; pagina: number; itens: TrilhaItem[]; proxima_pagina?: number | null; travessia?: boolean }>(r);
  },
  /** A Travessia (o mapa) deste livro. 404 = sem Travessia ou sem acesso. */
  async travessia(session: AuthSession | null, bookId: string) {
    const r = await apiFetchWithSession(session, `${API}/livros/${bookId}/travessia`, { cache: 'no-store' });
    return ok<TravessiaMapa>(r);
  },
  /** Os livros desta pessoa que têm Travessia (vazio sem acesso). */
  async travessiaLivros(session: AuthSession | null) {
    const r = await apiFetchWithSession(session, `${API}/travessia/livros`, { cache: 'no-store' });
    return ok<{ livros: Record<string, TravessiaResumo> }>(r);
  },
  /** A interface nova (Home, Explorar e Perfil) está liberada para esta conta? */
  async interfaceNova(session: AuthSession | null) {
    const r = await apiFetchWithSession(session, `${API}/interface`, { cache: 'no-store' });
    return ok<{ nova: boolean; trilhas?: boolean; pro?: boolean; etapa1?: boolean; paywall?: boolean; acesso?: AcessoApp }>(r);
  },
  /** A oferta pública (sem login): 'assinatura' = só assinantes, com garantia
   *  (TESTE_DIAS=0); 'teste' = N dias grátis; 'gratis' = paywall desligado. */
  async oferta() {
    const r = await fetch(`${API}/oferta`, { cache: 'no-store' });
    return ok<{
      teste_gratis: boolean; teste_dias: number | null;
      modelo?: 'gratis' | 'teste' | 'assinatura'; garantia_dias?: number | null;
      trilhas?: TrilhaDaOferta[];
      formacao?: { habilidades: number; areas: number; desafios: number; insights: number; etapas: number } | null;
      sugerida?: string | null;
    }>(r);
  },
  async curadoria(session: AuthSession | null) {
    const r = await apiFetchWithSession(session, `${API}/curadoria`, { cache: 'no-store' });
    return ok<Curadoria>(r);
  },
  async curadoriaFicha(session: AuthSession | null, slug: string) {
    const r = await apiFetchWithSession(session, `${API}/curadoria/livro/${encodeURIComponent(slug)}`, { cache: 'no-store' });
    return ok<CuradoriaFicha>(r);
  },
  async votarTravessia(session: AuthSession | null, slug: string, quero: boolean) {
    const r = await apiFetchWithSession(session, `${API}/curadoria/livro/${encodeURIComponent(slug)}/voto`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ quero }),
    });
    return ok<{ ok: true; slug: string; quero: boolean }>(r);
  },
  async comecarTravessia(session: AuthSession | null, slug: string) {
    const r = await apiFetchWithSession(session, `${API}/curadoria/livro/${encodeURIComponent(slug)}/comecar`, { method: 'POST' });
    return ok<{ book_id?: string; criar?: { title: string; author: string; totalPages: number; coverSourceUrl: string | null; isbn: string | null } }>(r);
  },
  async calendarioLista(session: AuthSession | null) {
    const r = await apiFetchWithSession(session, `${API}/lista/calendario`, { cache: 'no-store' });
    return ok<{ itens: { book_id: string; mes: string }[] }>(r);
  },
  async marcarMesLista(session: AuthSession | null, bookId: string, mes: string | null) {
    const r = await apiFetchWithSession(session, `${API}/lista/calendario/${bookId}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ mes }),
    });
    return ok<{ ok: true; book_id: string; mes: string | null }>(r);
  },
  /** Desafio de hoje: true = feito, false = não feito, null = desfaz. */
  async marcarDesafio(session: AuthSession | null, itemId: string, feito: boolean | null) {
    const r = await apiFetchWithSession(session, `${API}/trilha/${itemId}/desafio`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ feito }),
    });
    return ok<{ ok: true; item_id: string; estado: DesafioEstado }>(r);
  },
  /** Quantas notas do Mentor cada livro tem liberadas e ainda não abertas. */
  async trilhaNovas(session: AuthSession | null) {
    const r = await apiFetchWithSession(session, `${API}/trilha/novas`, { cache: 'no-store' });
    return ok<{ porLivro: Record<string, number> }>(r);
  },
  async marcarTrilhaVista(session: AuthSession | null, bookId: string) {
    const r = await apiFetchWithSession(session, `${API}/livros/${bookId}/trilha/visto`, { method: 'POST' });
    return ok<{ ok: true; marcados: number }>(r);
  },
  async avaliarTrilha(session: AuthSession | null, itemId: string, avaliacao: TrilhaAvaliacao | null) {
    const r = await apiFetchWithSession(session, `${API}/trilha/${itemId}/avaliar`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ avaliacao }),
    });
    return ok<{ ok: true; item_id: string; avaliacao: TrilhaAvaliacao | null }>(r);
  },

  // FP5 · Registrar um TRECHO (da/até) — não move o cursor linear; só soma e entra no mapa
  async logSegment(session: AuthSession | null, bookId: string, from: number, to: number, minutes?: number | null, label?: string | null) {
    const body: any = { from, to };
    if (minutes != null) body.minutes = minutes;
    if (label) body.label = label;
    const r = await apiFetchWithSession(session, `${API}/books/${bookId}/log-segment`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    return ok<{ book: BookV2; entry: any }>(r);
  },

  // Histórico de leitura — listar, editar, excluir
  // Toda edição/delete passa por syncBookProgressFromHistory no backend que
  // recalcula current_page do livro em cascata. UI vai refletir tudo via reload.
  async getReadingHistory(session: AuthSession | null): Promise<Array<{
    id: string;
    book_id: string;
    book_title: string;
    pages_read: number;
    new_page: number;
    reading_date: string;
    daily_goal: number;
    reading_time_minutes?: number;
    completed?: boolean;
  }>> {
    const r = await apiFetchWithSession(session, `${API}/reading-history`);
    return ok(r);
  },
  async updateReadingEntry(
    session: AuthSession | null,
    entryId: string,
    payload: { pages_read: number; reading_date: string }
  ): Promise<{ record: any; book: any }> {
    const r = await apiFetchWithSession(session, `${API}/reading-history/${entryId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return ok(r);
  },
  async deleteReadingEntry(
    session: AuthSession | null,
    entryId: string
  ): Promise<{ success: boolean; book: any }> {
    const r = await apiFetchWithSession(session, `${API}/reading-history/${entryId}`, {
      method: 'DELETE',
    });
    return ok(r);
  },

  // Legacy progress endpoint (pagesRead delta) — fallback
  async logReadingDelta(session: AuthSession | null, bookId: string, pagesRead: number) {
    const r = await apiFetchWithSession(session, `${API}/books/${bookId}/progress`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ pagesRead }),
    });
    return ok(r);
  },

  // Daily/weekly progress
  async getDailyProgress(session: AuthSession | null): Promise<DailyProgressV2> {
    const r = await apiFetchWithSession(session, `${API}/daily-progress`);
    return ok<DailyProgressV2>(r);
  },

  // Stats
  async getStreak(session: AuthSession | null): Promise<StreakData> {
    const r = await apiFetchWithSession(session, `${API}/stats/streak`);
    return ok<StreakData>(r);
  },
  async getHeatmap(session: AuthSession | null, weeks = 26): Promise<HeatmapData> {
    const r = await apiFetchWithSession(session, `${API}/stats/heatmap?weeks=${weeks}`);
    return ok<HeatmapData>(r);
  },
  async getAnalytics(session: AuthSession | null): Promise<AnalyticsData> {
    const r = await apiFetchWithSession(session, `${API}/stats/analytics`);
    return ok<AnalyticsData>(r);
  },
  async getAchievements(session: AuthSession | null): Promise<AchievementsResponse> {
    const r = await apiFetchWithSession(session, `${API}/achievements`);
    return ok<AchievementsResponse>(r);
  },
  async getInsights(session: AuthSession | null): Promise<InsightsResponse> {
    const r = await apiFetchWithSession(session, `${API}/insights`, { method: 'POST' });
    return ok<InsightsResponse>(r);
  },

  async getPushPublicKey(session: AuthSession | null) {
    const r = await apiFetchWithSession(session, `${API}/push/public-key`);
    return ok<{ publicKey: string }>(r);
  },
  async subscribePush(session: AuthSession | null, subscription: any) {
    // Backend espera payload flat: { endpoint, keys, expirationTime }
    const json = typeof subscription.toJSON === 'function' ? subscription.toJSON() : subscription;
    const payload = {
      endpoint: json.endpoint,
      keys: json.keys,
      expirationTime: json.expirationTime ?? null,
    };
    const r = await apiFetchWithSession(session, `${API}/push/subscribe`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return ok(r);
  },
  async testPush(session: AuthSession | null) {
    const r = await apiFetchWithSession(session, `${API}/push/test`, { method: 'POST' });
    return ok(r);
  },

  async getNotifSettings(session: AuthSession | null) {
    const r = await apiFetchWithSession(session, `${API}/notification-settings`);
    return ok<any>(r);
  },
  async putNotifSettings(session: AuthSession | null, settings: any) {
    const r = await apiFetchWithSession(session, `${API}/notification-settings`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(settings),
    });
    return ok(r);
  },

  async getDailyQuote(session: AuthSession | null) {
    const r = await apiFetchWithSession(session, `${API}/daily-quote`);
    return ok<{
      text: string;
      author: string;
      book: { id: string; title: string; author: string } | null;
      slot: { slot_date: string; slot_index: number; label: string };
      cached: boolean;
      source: string;
    }>(r);
  },

  // Cover management
  async searchCover(session: AuthSession | null, bookId: string) {
    const r = await apiFetchWithSession(session, `${API}/books/${bookId}/cover-search`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: '{}',
    });
    return ok<{ coverUrl: string; source: string }>(r);
  },
  async uploadCover(session: AuthSession | null, bookId: string, file: File) {
    const fd = new FormData();
    fd.append('cover', file);
    const r = await apiFetchWithSession(session, `${API}/books/${bookId}/cover-upload`, {
      method: 'POST',
      body: fd,
    });
    return ok<{ coverUrl: string }>(r);
  },
  async setCoverUrl(session: AuthSession | null, bookId: string, coverUrl: string) {
    const r = await apiFetchWithSession(session, `${API}/books/${bookId}/cover`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ coverUrl }),
    });
    return ok(r);
  },

  // Admin
  // Checkout / billing — Mercado Pago (Preapproval API).
  // `init_point` é a URL pública do MP onde o user autoriza a subscription.
  // Frontend faz `window.location.href = init_point`.
  async startCheckout(session: AuthSession | null, payload: { planId: string; period: 'month' | 'year'; paymentMethod?: 'card' | 'pix' }): Promise<{
    ok: boolean;
    provider: string;
    preapproval_id: string;
    init_point: string;
    status: string;
    amount: number;
    period: string;
  }> {
    const r = await apiFetchWithSession(session, `${API}/checkout/start-mp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return ok(r);
  },
  async cancelCheckout(session: AuthSession | null): Promise<{ ok: boolean; cancelled: boolean; plan: string; plan_expires_at: string | null; message: string }> {
    const r = await apiFetchWithSession(session, `${API}/checkout/cancel-mp`, { method: 'POST' });
    return ok(r);
  },
  // PIX 1-shot anual customizado — backend gera QR via Payments API, frontend
  // renderiza dentro do app (sem redirect pra MP).
  async startCheckoutPix(session: AuthSession | null, payload: { planId: string; period: 'year' }): Promise<{
    ok: boolean;
    provider: string;
    method: 'pix';
    payment_id: string;
    amount: number;
    qr_code: string;
    qr_code_base64: string;
    ticket_url: string;
    expires_at: string;
    plan_id: string;
    plan_label: string;
  }> {
    const r = await apiFetchWithSession(session, `${API}/checkout/start-mp-pix`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return ok(r);
  },
  async getPixStatus(session: AuthSession | null, paymentId: string): Promise<{ payment_id: string; status: string; paid: boolean; plano_ativo?: boolean; amount: number; date_approved: string | null }> {
    const r = await apiFetchWithSession(session, `${API}/checkout/pix-status/${encodeURIComponent(paymentId)}`);
    return ok(r);
  },
  async getMySubscription(session: AuthSession | null): Promise<{
    plan: string;
    plan_status: string;
    plan_expires_at: string | null;
    subscription: { id: string; status: string; cycle: string; value: number; billingType: string; nextDueDate: string; dateCreated: string; deleted: boolean } | null;
    nextPayment: { id: string; dueDate: string; value: number; billingType: string; status: string; invoiceUrl: string } | null;
    recentEvents: Array<{ event_type: string; plan_id: string | null; billing_period: string | null; payment_method: string | null; amount: number; status: string; created_at: string }>;
  }> {
    const r = await apiFetchWithSession(session, `${API}/me/subscription`);
    return ok(r);
  },
  async adminListSubscriptionEvents(session: AuthSession | null, limit = 100): Promise<{ events: any[] }> {
    const r = await apiFetchWithSession(session, `${API}/admin/subscription-events?limit=${limit}`);
    return ok(r);
  },

  // Plans (público — frontend lê pra montar paywall)
  async listPlans(session: AuthSession | null): Promise<{ plans: any[] }> {
    const r = await apiFetchWithSession(session, `${API}/plans`);
    return ok(r);
  },
  // Admin plans (CRUD)
  async adminListPlans(session: AuthSession | null): Promise<{ plans: any[] }> {
    const r = await apiFetchWithSession(session, `${API}/admin/plans`);
    return ok(r);
  },
  async adminUpdatePlan(session: AuthSession | null, planId: string, patch: any): Promise<{ plan: any }> {
    const r = await apiFetchWithSession(session, `${API}/admin/plans/${planId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    });
    return ok(r);
  },

  // Console admin: dashboard agregado (KPIs)
  async adminDashboard(session: AuthSession | null): Promise<any> {
    const r = await apiFetchWithSession(session, `${API}/admin/dashboard/overview`);
    return ok(r);
  },
  async adminTimeseries(session: AuthSession | null, days = 30): Promise<{ series: any[]; days: number }> {
    const r = await apiFetchWithSession(session, `${API}/admin/dashboard/timeseries?days=${days}`);
    return ok(r);
  },
  async adminListClubs(session: AuthSession | null): Promise<{ clubs: any[] }> {
    const r = await apiFetchWithSession(session, `${API}/admin/dashboard/clubs`);
    return ok(r);
  },

  async adminSetPlan(session: AuthSession | null, userId: string, plan: 'free' | 'reader' | 'reader_plus' | 'leader' | 'leader_pro') {
    const r = await apiFetchWithSession(session, `${API}/admin/users/${userId}/plan`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ plan }),
    });
    return ok(r);
  },

  // ─────────────────────────────────────────────────────────────────────────
  // CLUBES / GRUPOS
  // ─────────────────────────────────────────────────────────────────────────
  async getClubs(session: AuthSession | null): Promise<{ clubs: any[]; pendingInvites: any[] }> {
    const r = await apiFetchWithSession(session, `${API}/clubs`);
    return ok(r);
  },
  async exploreClubs(session: AuthSession | null, opts: { q?: string; mode?: string; genre?: string; sort?: 'recent' | 'popular'; limit?: number } = {}): Promise<{ clubs: any[] }> {
    const qs = new URLSearchParams();
    if (opts.q) qs.set('q', opts.q);
    if (opts.mode) qs.set('mode', opts.mode);
    if (opts.genre) qs.set('genre', opts.genre);
    if (opts.sort) qs.set('sort', opts.sort);
    qs.set('limit', String(opts.limit ?? 24));
    const r = await apiFetchWithSession(session, `${API}/clubs/explore?${qs}`);
    return ok(r);
  },
  async joinClub(session: AuthSession | null, clubId: string): Promise<{ ok: boolean; clubId: string; alreadyMember?: boolean }> {
    const r = await apiFetchWithSession(session, API + '/clubs/' + clubId + '/join', { method: 'POST' });
    return ok(r);
  },
  async getClub(session: AuthSession | null, clubId: string, viewingUserId?: string | null): Promise<any> {
    const qs = viewingUserId ? `?viewingUserId=${encodeURIComponent(viewingUserId)}` : '';
    const r = await apiFetchWithSession(session, `${API}/clubs/${clubId}${qs}`);
    return ok(r);
  },
  async createClub(session: AuthSession | null, payload: any): Promise<any> {
    const r = await apiFetchWithSession(session, `${API}/clubs`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return ok(r);
  },
  // ── Clube Social: proposta viva (co-criação) ──
  async listMyProposals(session: AuthSession | null): Promise<{ proposals: any[] }> {
    const r = await apiFetchWithSession(session, `${API}/clubs/proposals/mine`);
    return ok(r);
  },
  async getClubProposal(session: AuthSession | null, clubId: string): Promise<any> {
    const r = await apiFetchWithSession(session, `${API}/clubs/${encodeURIComponent(clubId)}/proposal`);
    return ok(r);
  },
  async patchClubProposal(session: AuthSession | null, clubId: string, patch: any): Promise<any> {
    const r = await apiFetchWithSession(session, `${API}/clubs/${encodeURIComponent(clubId)}/proposal`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(patch),
    });
    return ok(r);
  },
  async proposalInterest(session: AuthSession | null, clubId: string): Promise<any> {
    const r = await apiFetchWithSession(session, `${API}/clubs/${encodeURIComponent(clubId)}/proposal/interest`, { method: 'POST' });
    return ok(r);
  },
  async proposalDecline(session: AuthSession | null, clubId: string): Promise<any> {
    const r = await apiFetchWithSession(session, `${API}/clubs/${encodeURIComponent(clubId)}/proposal/decline`, { method: 'POST' });
    return ok(r);
  },
  async proposalLeave(session: AuthSession | null, clubId: string): Promise<any> {
    const r = await apiFetchWithSession(session, `${API}/clubs/${encodeURIComponent(clubId)}/proposal/leave`, { method: 'POST' });
    return ok(r);
  },
  async proposalLaunch(session: AuthSession | null, clubId: string, body: any = {}): Promise<any> {
    const r = await apiFetchWithSession(session, `${API}/clubs/${encodeURIComponent(clubId)}/proposal/launch`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
    });
    return ok(r);
  },
  async proposalReserve(session: AuthSession | null, clubId: string): Promise<any> {
    const r = await apiFetchWithSession(session, `${API}/clubs/${encodeURIComponent(clubId)}/proposal/reserve`, { method: 'POST' });
    return ok(r);
  },
  async listProposalComments(session: AuthSession | null, clubId: string): Promise<{ comments: any[]; isOwner: boolean }> {
    const r = await apiFetchWithSession(session, `${API}/clubs/${encodeURIComponent(clubId)}/proposal/comments`);
    return ok(r);
  },
  async addProposalComment(session: AuthSession | null, clubId: string, text: string, parentId?: string | null): Promise<any> {
    const r = await apiFetchWithSession(session, `${API}/clubs/${encodeURIComponent(clubId)}/proposal/comments`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ text, parentId: parentId || null }),
    });
    return ok(r);
  },
  async editProposalComment(session: AuthSession | null, clubId: string, cid: string, text: string): Promise<any> {
    const r = await apiFetchWithSession(session, `${API}/clubs/${encodeURIComponent(clubId)}/proposal/comments/${encodeURIComponent(cid)}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ text }),
    });
    return ok(r);
  },
  async deleteProposalComment(session: AuthSession | null, clubId: string, cid: string): Promise<any> {
    const r = await apiFetchWithSession(session, `${API}/clubs/${encodeURIComponent(clubId)}/proposal/comments/${encodeURIComponent(cid)}`, { method: 'DELETE' });
    return ok(r);
  },
  async likeProposalComment(session: AuthSession | null, clubId: string, cid: string): Promise<{ liked: boolean; count: number }> {
    const r = await apiFetchWithSession(session, `${API}/clubs/${encodeURIComponent(clubId)}/proposal/comments/${encodeURIComponent(cid)}/like`, { method: 'POST' });
    return ok(r);
  },
  // SSE em tempo real: lê o stream via fetch (com auth) e chama onEvent(event, data).
  // Retorna uma função de cancelamento. Fallback: se streaming não rolar, o caller faz polling.
  openProposalStream(session: AuthSession | null, clubId: string, onEvent: (event: string, data: any) => void): () => void {
    return sseStream(session, `${API}/clubs/${encodeURIComponent(clubId)}/proposal/stream`, onEvent);
  },

  // ── Mural do clube (conversa SOBRE o clube, não sobre o livro) ──
  // Mesmo contrato dos comentários da proposta; ver server/clubs/mural-routes.js.
  async listMural(session: AuthSession | null, clubId: string): Promise<{ comments: any[]; podeModerar: boolean; indisponivel?: boolean }> {
    const r = await apiFetchWithSession(session, `${API}/clubs/${encodeURIComponent(clubId)}/mural`);
    return ok(r);
  },
  async addMural(session: AuthSession | null, clubId: string, text: string, parentId?: string | null): Promise<any> {
    const r = await apiFetchWithSession(session, `${API}/clubs/${encodeURIComponent(clubId)}/mural`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ text, parentId: parentId || null }),
    });
    return ok(r);
  },
  async editMural(session: AuthSession | null, clubId: string, cid: string, text: string): Promise<any> {
    const r = await apiFetchWithSession(session, `${API}/clubs/${encodeURIComponent(clubId)}/mural/${encodeURIComponent(cid)}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ text }),
    });
    return ok(r);
  },
  async deleteMural(session: AuthSession | null, clubId: string, cid: string): Promise<any> {
    const r = await apiFetchWithSession(session, `${API}/clubs/${encodeURIComponent(clubId)}/mural/${encodeURIComponent(cid)}`, { method: 'DELETE' });
    return ok(r);
  },
  async likeMural(session: AuthSession | null, clubId: string, cid: string): Promise<{ liked: boolean; count: number }> {
    const r = await apiFetchWithSession(session, `${API}/clubs/${encodeURIComponent(clubId)}/mural/${encodeURIComponent(cid)}/like`, { method: 'POST' });
    return ok(r);
  },
  openMuralStream(session: AuthSession | null, clubId: string, onEvent: (event: string, data: any) => void): () => void {
    return sseStream(session, `${API}/clubs/${encodeURIComponent(clubId)}/mural/stream`, onEvent);
  },

  // Upload de capa pra clube — multipart com campo 'cover'. Retorna URL salva
  // em /uploads/covers/clubs/. Usado no create club + edit club.
  async uploadClubCover(session: AuthSession | null, file: File): Promise<{ url: string }> {
    const fd = new FormData();
    fd.append('cover', file);
    const r = await apiFetchWithSession(session, `${API}/clubs/cover-upload`, {
      method: 'POST',
      body: fd,
      // NÃO setar Content-Type — browser cuida do boundary do multipart
    });
    return ok(r);
  },
  async patchClub(session: AuthSession | null, clubId: string, patch: any): Promise<any> {
    const r = await apiFetchWithSession(session, `${API}/clubs/${clubId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    });
    return ok(r);
  },
  async deleteClub(session: AuthSession | null, clubId: string): Promise<{ ok: boolean }> {
    const r = await apiFetchWithSession(session, `${API}/clubs/${clubId}`, { method: 'DELETE' });
    return ok(r);
  },
  async leaveClub(session: AuthSession | null, clubId: string): Promise<{ ok: boolean }> {
    const r = await apiFetchWithSession(session, `${API}/clubs/${clubId}/leave`, { method: 'POST' });
    return ok(r);
  },
  async setClubBook(session: AuthSession | null, clubId: string, book: { title: string; author: string; totalPages: number; coverUrl?: string | null; coverHue?: number; coverStyle?: string; startDate?: string; endDate?: string }): Promise<any> {
    const r = await apiFetchWithSession(session, `${API}/clubs/${clubId}/book`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(book),
    });
    return ok(r);
  },
  async patchClubBook(session: AuthSession | null, clubId: string, patch: { title?: string; author?: string; totalPages?: number; startPage?: number; coverUrl?: string | null; coverHue?: number; coverStyle?: string; buyUrl?: string | null }): Promise<any> {
    const r = await apiFetchWithSession(session, `${API}/clubs/${clubId}/book`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    });
    return ok(r);
  },
  // Espelha o livro do clube no perfil do usuário (cria se não existir) e
  // retorna o BookV2 pessoal. Sem bookId espelha o atual; com bookId espelha
  // o livro especifico da fila (planned/active/finished).
  async startReadingClubBook(session: AuthSession | null, clubId: string, bookId?: string | null): Promise<{ book: any }> {
    const r = await apiFetchWithSession(session, `${API}/clubs/${clubId}/start-reading`, {
      method: 'POST',
      headers: bookId ? { 'Content-Type': 'application/json' } : undefined,
      body: bookId ? JSON.stringify({ bookId }) : undefined,
    });
    return ok(r);
  },

  // Multi-livro: marca o livro atual como finalizado e promove o proximo planned.
  async finishClubBook(session: AuthSession | null, clubId: string, bookId: string): Promise<{ ok: boolean; nextBookId: string | null }> {
    const r = await apiFetchWithSession(session, `${API}/clubs/${clubId}/books/${bookId}/finish`, { method: 'POST' });
    return ok(r);
  },

  // Edita qualquer livro da fila (admin) — usado pelo tap no coverflow do clube.
  async patchClubBookById(session: AuthSession | null, clubId: string, bookId: string, patch: { title?: string; author?: string; totalPages?: number; startPage?: number; coverUrl?: string | null; coverHue?: number; coverStyle?: string; buyUrl?: string | null }): Promise<any> {
    const r = await apiFetchWithSession(session, `${API}/clubs/${clubId}/books/${bookId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    });
    return ok(r);
  },

  // ─────────────────────────────────────────────────────────────────────────
  // CLUBE AVULSO (shared-habit) — fila individual de livros por membro
  // Endpoints novos em /api/clubs/:id/member-books — cada user so edita o
  // proprio (admin nao pode editar fila alheia, so marcos gerais).
  // ─────────────────────────────────────────────────────────────────────────
  async listMemberBooks(session: AuthSession | null, clubId: string, userId?: string): Promise<{ ok: boolean; userId: string; isMine: boolean; books: any[] }> {
    const qs = userId ? `?userId=${encodeURIComponent(userId)}` : '';
    const r = await apiFetchWithSession(session, `${API}/clubs/${clubId}/member-books${qs}`);
    return ok(r);
  },
  async addMemberBook(session: AuthSession | null, clubId: string, book: { title: string; author?: string; totalPages: number; dailyGoal?: number; coverUrl?: string | null; coverHue?: number; coverStyle?: string; targetDate?: string | null; progressUnit?: 'pages' | 'chapters' | 'positions' }): Promise<any> {
    const r = await apiFetchWithSession(session, `${API}/clubs/${clubId}/member-books`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ book }),
    });
    return ok(r);
  },
  async patchMemberBook(session: AuthSession | null, clubId: string, mbId: string, patch: any): Promise<any> {
    const r = await apiFetchWithSession(session, `${API}/clubs/${clubId}/member-books/${mbId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    });
    return ok(r);
  },
  async deleteMemberBook(session: AuthSession | null, clubId: string, mbId: string): Promise<any> {
    const r = await apiFetchWithSession(session, `${API}/clubs/${clubId}/member-books/${mbId}`, { method: 'DELETE' });
    return ok(r);
  },
  async finishMemberBook(session: AuthSession | null, clubId: string, mbId: string): Promise<{ ok: boolean; promoted: string | null }> {
    const r = await apiFetchWithSession(session, `${API}/clubs/${clubId}/member-books/${mbId}/finish`, { method: 'POST' });
    return ok(r);
  },
  async reorderMemberBooks(session: AuthSession | null, clubId: string, ids: string[]): Promise<any> {
    const r = await apiFetchWithSession(session, `${API}/clubs/${clubId}/member-books/reorder`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ids }),
    });
    return ok(r);
  },

  // ─────────────────────────────────────────────────────────────────────────
  // FEED DO ESTUDO — marcos, posts, reações e comentários
  // ─────────────────────────────────────────────────────────────────────────
  async listMilestones(session: AuthSession | null, clubId: string, opts: { bookId?: string | null } = {}): Promise<{ milestones: any[] }> {
    const qs = opts.bookId ? `?bookId=${encodeURIComponent(opts.bookId)}` : '';
    const r = await apiFetchWithSession(session, `${API}/clubs/${clubId}/milestones${qs}`);
    return ok(r);
  },
  async createMilestone(session: AuthSession | null, clubId: string, payload: { label: string; targetPage: number; deadline?: string; chapterRef?: string }): Promise<any> {
    const r = await apiFetchWithSession(session, `${API}/clubs/${clubId}/milestones`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return ok(r);
  },
  async closeMilestone(session: AuthSession | null, clubId: string, milestoneId: string): Promise<any> {
    const r = await apiFetchWithSession(session, `${API}/clubs/${clubId}/milestones/${milestoneId}/close`, { method: 'POST' });
    return ok(r);
  },

  async listPosts(session: AuthSession | null, clubId: string, opts: { milestone?: string; filter?: string; limit?: number; before?: string; bookId?: string | null; ordem?: 'espinha' | 'recentes'; cursorPage?: number | null; cursorAt?: string | null } = {}): Promise<{ posts: any[]; nextCursor: string | null; proximo?: { page?: number; at: string } | null; fim?: boolean }> {
    const qs = new URLSearchParams();
    if (opts.milestone) qs.set('milestone', opts.milestone);
    if (opts.filter) qs.set('filter', opts.filter);
    if (opts.limit) qs.set('limit', String(opts.limit));
    if (opts.before) qs.set('before', opts.before);
    if (opts.bookId) qs.set('bookId', opts.bookId);
    // A espinha ordena pela PÁGINA DO LIVRO, e o cursor é composto porque várias
    // publicações dividem a mesma página.
    if (opts.ordem) qs.set('ordem', opts.ordem);
    if (opts.cursorPage !== undefined && opts.cursorPage !== null) qs.set('cursorPage', String(opts.cursorPage));
    if (opts.cursorAt) qs.set('cursorAt', opts.cursorAt);
    const r = await apiFetchWithSession(session, `${API}/clubs/${clubId}/posts?${qs}`);
    return ok(r);
  },
  // As publicações sem âncora de página não cabem na espinha e viriam a sumir.
  async listPostsAvulsos(session: AuthSession | null, clubId: string, opts: { limit?: number; before?: string; bookId?: string | null } = {}): Promise<{ posts: any[]; proximo: { at: string } | null; fim: boolean }> {
    const qs = new URLSearchParams();
    if (opts.limit) qs.set('limit', String(opts.limit));
    if (opts.before) qs.set('before', opts.before);
    if (opts.bookId) qs.set('bookId', opts.bookId);
    const r = await apiFetchWithSession(session, `${API}/clubs/${clubId}/posts/avulsos?${qs}`);
    return ok(r);
  },
  // Tempo real do Estudo. Mesma mecânica do stream da proposta de clube: lê o
  // SSE por fetch (porque `EventSource` não manda cabeçalho de autenticação) e
  // devolve a função de cancelamento.
  openEstudoStream(session: AuthSession | null, clubId: string, onEvent: (event: string, data: any) => void): () => void {
    const ctrl = new AbortController();
    (async () => {
      try {
        const r = await apiFetchWithSession(session, `${API}/clubs/${encodeURIComponent(clubId)}/estudo/stream`, {
          signal: ctrl.signal, headers: { Accept: 'text/event-stream' },
        });
        if (!r.body || !r.body.getReader) return;
        const reader = r.body.getReader();
        const dec = new TextDecoder();
        let buf = '';
        // eslint-disable-next-line no-constant-condition
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buf += dec.decode(value, { stream: true });
          let idx;
          while ((idx = buf.indexOf('\n\n')) >= 0) {
            const chunk = buf.slice(0, idx); buf = buf.slice(idx + 2);
            let ev = 'message'; let data = '';
            chunk.split('\n').forEach(line => {
              if (line.startsWith('event:')) ev = line.slice(6).trim();
              else if (line.startsWith('data:')) data += line.slice(5).trim();
            });
            if (data) { try { onEvent(ev, JSON.parse(data)); } catch {} }
          }
        }
      } catch { /* rede caiu: a tela recarrega ao voltar o foco */ }
    })();
    return () => { try { ctrl.abort(); } catch {} };
  },
  async uploadClubPostAudio(session: AuthSession | null, blob: Blob, filename: string): Promise<{ url: string }> {
    const fd = new FormData();
    fd.append('audio', blob, filename);
    const r = await apiFetchWithSession(session, `${API}/club-posts/audio`, { method: 'POST', body: fd });
    return ok(r);
  },
  async uploadClubPostPhoto(session: AuthSession | null, file: File): Promise<{ url: string }> {
    const fd = new FormData();
    fd.append('photo', file, file.name || 'foto.jpg');
    const r = await apiFetchWithSession(session, `${API}/club-posts/photo`, { method: 'POST', body: fd });
    return ok(r);
  },
  async createPost(session: AuthSession | null, clubId: string, payload: { kind: string; content: any; page?: number; milestoneId?: string; parentPostId?: string }): Promise<{ post: any }> {
    const r = await apiFetchWithSession(session, `${API}/clubs/${clubId}/posts`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return ok(r);
  },
  async deletePost(session: AuthSession | null, clubId: string, postId: string): Promise<{ ok: boolean }> {
    const r = await apiFetchWithSession(session, `${API}/clubs/${clubId}/posts/${postId}`, { method: 'DELETE' });
    return ok(r);
  },
  async updatePost(session: AuthSession | null, clubId: string, postId: string, content: any): Promise<{ post: any }> {
    const r = await apiFetchWithSession(session, `${API}/clubs/${clubId}/posts/${postId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ content }),
    });
    return ok(r);
  },
  async reactToPost(session: AuthSession | null, clubId: string, postId: string, kind: 'tocou' | 'aprendi' | 'fogo' | 'salvar'): Promise<{ active: boolean; kind: string; count: number }> {
    const r = await apiFetchWithSession(session, `${API}/clubs/${clubId}/posts/${postId}/react`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ kind }),
    });
    return ok(r);
  },
  async voteQuiz(session: AuthSession | null, clubId: string, postId: string, questionId: string, optionId: string): Promise<{ ok: boolean; questionId: string; yourVote: string; options: any[]; totalVotes: number }> {
    const r = await apiFetchWithSession(session, `${API}/clubs/${clubId}/posts/${postId}/vote`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ questionId, optionId }),
    });
    return ok(r);
  },

  // ─────────────────────────────────────────────────────────────────────────
  // CAIXA DE PERGUNTAS (qbox) — perguntas anônimas + resposta do líder
  // ─────────────────────────────────────────────────────────────────────────
  async listQuestionBoxes(session: AuthSession | null, clubId: string): Promise<{ qboxes: any[] }> {
    const r = await apiFetchWithSession(session, `${API}/clubs/${clubId}/qboxes`);
    return ok(r);
  },

  // Painel do líder — agregações sobre o clube (admin/mod only)
  async getClubAnalytics(session: AuthSession | null, clubId: string): Promise<{
    members: { total: number; active7d: number; inactive30d: number };
    reading: { weekPagesRead: number; prevWeekPagesRead: number; weekVsPrevPct: number; avgVelocity: number };
    engagement: { posts30d: number; reactionsTotal: number; commentsTotal: number; qboxOpen: number };
    heatmap: { byDayOfWeek: number[]; byHour: number[] };
    atRisk: any[];
  }> {
    const r = await apiFetchWithSession(session, `${API}/clubs/${clubId}/analytics`);
    return ok(r);
  },
  async askQuestionBox(session: AuthSession | null, clubId: string, postId: string, text: string): Promise<{ ok: boolean; question: any }> {
    const r = await apiFetchWithSession(session, `${API}/clubs/${clubId}/posts/${postId}/qbox/ask`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text }),
    });
    return ok(r);
  },
  async listQuestionBoxQuestions(session: AuthSession | null, clubId: string, postId: string): Promise<{ questions: any[]; total: number; prompt: string; closesIn: string | null }> {
    const r = await apiFetchWithSession(session, `${API}/clubs/${clubId}/posts/${postId}/qbox/questions`);
    return ok(r);
  },
  async answerQuestionBoxQuestion(session: AuthSession | null, clubId: string, postId: string, questionId: string, text: string): Promise<{ ok: boolean; questionId: string; answerPostId: string }> {
    const r = await apiFetchWithSession(session, `${API}/clubs/${clubId}/posts/${postId}/qbox/${questionId}/answer`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text }),
    });
    return ok(r);
  },
  async deleteQuestionBoxQuestion(session: AuthSession | null, clubId: string, postId: string, questionId: string): Promise<{ ok: boolean }> {
    const r = await apiFetchWithSession(session, `${API}/clubs/${clubId}/posts/${postId}/qbox/${questionId}`, { method: 'DELETE' });
    return ok(r);
  },

  // ─────────────────────────────────────────────────────────────────────────
  // POKES — admin/mod cutuca membro pra incentivar engajamento
  // ─────────────────────────────────────────────────────────────────────────
  // ─────────────────────────────────────────────────────────────────────────
  // MODERACAO UGC (Apple Guideline 1.2): denuncias e remocao de membros
  // ─────────────────────────────────────────────────────────────────────────
  async reportClub(session: AuthSession | null, clubId: string, reason: string): Promise<{ ok: boolean; id: string }> {
    const r = await apiFetchWithSession(session, `${API}/clubs/${clubId}/report`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason }),
    });
    return ok(r);
  },
  async reportPost(session: AuthSession | null, postId: string, reason: string): Promise<{ ok: boolean; id: string }> {
    const r = await apiFetchWithSession(session, `${API}/posts/${postId}/report`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason }),
    });
    return ok(r);
  },
  async removeClubMember(session: AuthSession | null, clubId: string, userId: string): Promise<{ ok: boolean }> {
    const r = await apiFetchWithSession(session, `${API}/clubs/${clubId}/members/${userId}`, {
      method: 'DELETE',
    });
    return ok(r);
  },

  async createPoke(session: AuthSession | null, clubId: string, payload: { toUserId: string; type?: string; message?: string }): Promise<{ ok: boolean; poke: any }> {
    const r = await apiFetchWithSession(session, `${API}/clubs/${clubId}/pokes`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return ok(r);
  },
  async listPokesInbox(session: AuthSession | null, opts: { onlyUnread?: boolean } = {}): Promise<{ pokes: any[] }> {
    const qs = opts.onlyUnread ? '?onlyUnread=1' : '';
    const r = await apiFetchWithSession(session, `${API}/pokes/inbox${qs}`);
    return ok(r);
  },
  async markPokeRead(session: AuthSession | null, pokeId: string): Promise<{ ok: boolean }> {
    const r = await apiFetchWithSession(session, `${API}/pokes/${pokeId}/read`, { method: 'POST' });
    return ok(r);
  },
  async markAllPokesRead(session: AuthSession | null): Promise<{ ok: boolean }> {
    const r = await apiFetchWithSession(session, `${API}/pokes/inbox/read-all`, { method: 'POST' });
    return ok(r);
  },
  async listComments(session: AuthSession | null, clubId: string, postId: string): Promise<{ comments: any[] }> {
    const r = await apiFetchWithSession(session, `${API}/clubs/${clubId}/posts/${postId}/comments`);
    return ok(r);
  },
  async createComment(session: AuthSession | null, clubId: string, postId: string, text: string, parentCommentId?: string): Promise<{ comment: any }> {
    const r = await apiFetchWithSession(session, `${API}/clubs/${clubId}/posts/${postId}/comments`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text, parentCommentId }),
    });
    return ok(r);
  },
  async updateComment(session: AuthSession | null, clubId: string, postId: string, commentId: string, text: string): Promise<{ comment: any }> {
    const r = await apiFetchWithSession(session, `${API}/clubs/${clubId}/posts/${postId}/comments/${commentId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text }),
    });
    return ok(r);
  },
  async reactToComment(session: AuthSession | null, clubId: string, postId: string, commentId: string, kind: 'tocou' | 'fogo'): Promise<{ ok: boolean; active: boolean; kind: string; count: number }> {
    const r = await apiFetchWithSession(session, `${API}/clubs/${clubId}/posts/${postId}/comments/${commentId}/react`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ kind }),
    });
    return ok(r);
  },
  async deleteComment(session: AuthSession | null, clubId: string, postId: string, commentId: string): Promise<{ ok: boolean }> {
    const r = await apiFetchWithSession(session, `${API}/clubs/${clubId}/posts/${postId}/comments/${commentId}`, { method: 'DELETE' });
    return ok(r);
  },

  // Convites
  async createInvite(session: AuthSession | null, clubId: string, opts: { targetEmail?: string; maxUses?: number; expiresInDays?: number } = {}): Promise<{ id: string; token: string; url: string; targetEmail: string | null; maxUses: number | null; expiresAt: string }> {
    const r = await apiFetchWithSession(session, `${API}/clubs/${clubId}/invites`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(opts),
    });
    return ok(r);
  },
  async listInvites(session: AuthSession | null, clubId: string): Promise<{ invites: any[] }> {
    const r = await apiFetchWithSession(session, `${API}/clubs/${clubId}/invites`);
    return ok(r);
  },
  async revokeInvite(session: AuthSession | null, clubId: string, inviteId: string): Promise<{ ok: boolean }> {
    const r = await apiFetchWithSession(session, `${API}/clubs/${clubId}/invites/${inviteId}`, { method: 'DELETE' });
    return ok(r);
  },
  async acceptInvite(session: AuthSession | null, token: string): Promise<{ ok: boolean; clubId: string }> {
    const r = await apiFetchWithSession(session, `${API}/invites/${token}/accept`, { method: 'POST' });
    return ok(r);
  },
  async rejectInvite(session: AuthSession | null, token: string): Promise<{ ok: boolean }> {
    const r = await apiFetchWithSession(session, `${API}/invites/${token}/reject`, { method: 'POST' });
    return ok(r);
  },
  // Preview público (sem auth) — usado na landing /c/:token
  async getInvitePreview(token: string): Promise<any> {
    const r = await fetch(`${API}/public/invites/${token}`);
    return ok(r);
  },
  // Preview público de perfil (sem auth) — usado na landing /@handle
  async getProfilePreview(handle: string): Promise<any> {
    const r = await fetch(`${API}/public/profile/${encodeURIComponent(String(handle).replace(/^@/, ''))}`);
    return ok(r);
  },

  // Termo (clube/grupo)
  async setPreferredTerm(session: AuthSession | null, term: 'clube' | 'grupo'): Promise<{ preferredTerm: 'clube' | 'grupo' }> {
    const r = await apiFetchWithSession(session, `${API}/profile/preferred-term`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ preferredTerm: term }),
    });
    return ok(r);
  },
};

export function statusToDb(s: 'reading' | 'finished' | 'wishlist' | 'paused'): 'lendo' | 'lido' | 'proximo' | 'parado' {
  if (s === 'reading') return 'lendo';
  if (s === 'finished') return 'lido';
  if (s === 'paused') return 'parado';
  return 'proximo';
}

export function statusToDesign(s: 'lendo' | 'lido' | 'proximo' | 'parado'): 'reading' | 'finished' | 'wishlist' | 'paused' {
  if (s === 'lendo') return 'reading';
  if (s === 'lido') return 'finished';
  if (s === 'parado') return 'paused';
  return 'wishlist';
}
